import path from "path";
import fs from "fs";
import { findPackageJson, safeReadJson } from "../utils/files.js";
import { isDependencyNative } from "../analyze/dependencyAnalyzer.js";
import { loadPluginsForProject } from "../plugins/loader.js";
import { PluginRegistry } from "../plugins/registry.js";
import type { DependencyGraph, DependencyNode, NodeType } from "../models/DependencyGraph.js";

/**
 * Reads the direct dependencies of a package from its own package.json
 * inside node_modules. Returns an empty array if not available.
 */
function readPackageDependencies(projectRoot: string, packageName: string): string[] {
  try {
    const pkgPath = path.join(projectRoot, "node_modules", packageName, "package.json");
    if (!fs.existsSync(pkgPath)) return [];
    const raw = fs.readFileSync(pkgPath, "utf-8");
    const data = JSON.parse(raw);
    return Object.keys(data.dependencies || {});
  } catch {
    return [];
  }
}

/**
 * Builds a flat DependencyGraph from package.json.
 *
 * Nodes represent top-level dependencies only (not transitive),
 * but each node includes its own first-level dependencies list for
 * graph traversal and visualization.
 */
export async function buildDependencyGraph(cwd: string): Promise<DependencyGraph> {
  const packageJsonPath = findPackageJson(cwd);
  if (!packageJsonPath) {
    throw new Error("package.json not found.");
  }

  const pkg = safeReadJson(packageJsonPath);
  if (!pkg) throw new Error("package.json is corrupted or unreadable.");

  const projectRoot = path.dirname(packageJsonPath);
  const projectName = (pkg["name"] as string) || path.basename(projectRoot);

  const dependencies = (pkg["dependencies"] as Record<string, string>) || {};
  const devDependencies = (pkg["devDependencies"] as Record<string, string>) || {};

  // Load plugins to know which packages have plugin support
  await loadPluginsForProject(projectRoot);
  const loadedPluginNames = new Set(PluginRegistry.getAll().map((p) => p.packageName));

  const rnVersionSpec = (dependencies["react-native"] as string) || "";
  const reactNativeVersion = rnVersionSpec.replace(/^[^0-9]*/, "") || "unknown";

  const nodes: DependencyNode[] = [];

  // Process production dependencies
  for (const [name, version] of Object.entries(dependencies)) {
    const isNative = name !== "react-native" && isDependencyNative(projectRoot, name);
    const hasPlugin = loadedPluginNames.has(name);

    let type: NodeType = "production";
    if (isNative) type = "native";
    else if (hasPlugin) type = "plugin";

    const deps = readPackageDependencies(projectRoot, name);

    nodes.push({
      name,
      version: version.replace(/^[^0-9]*/, "") || version,
      type,
      isNative,
      hasPlugin,
      dependencies: deps,
    });
  }

  // Process dev dependencies
  for (const [name, version] of Object.entries(devDependencies)) {
    const hasPlugin = loadedPluginNames.has(name);
    nodes.push({
      name,
      version: version.replace(/^[^0-9]*/, "") || version,
      type: "dev",
      isNative: false,
      hasPlugin,
      dependencies: [],
    });
  }

  // Sort nodes deterministically: production first, then dev; within each group by name
  nodes.sort((a, b) => {
    if (a.type !== "dev" && b.type === "dev") return -1;
    if (a.type === "dev" && b.type !== "dev") return 1;
    return a.name.localeCompare(b.name);
  });

  const nativeCount = nodes.filter((n) => n.isNative).length;
  const pluginCount = nodes.filter((n) => n.hasPlugin).length;

  return {
    projectName,
    reactNativeVersion,
    totalNodes: nodes.length,
    nativeCount,
    pluginCount,
    nodes,
    generatedAt: new Date().toISOString(),
  };
}
